import Script from 'next/script';
import { useEffect } from 'react';

interface GoogleOneTapProps {
  clientId: string;
  callback?: (response: { credential: string }) => void;
  loginUri?: string;
  context?: 'signin' | 'signup' | 'use';
  cancelOnTapOutside?: boolean;
  autoSelect?: boolean;
  momentCallback?: (notification: any) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          cancel: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

const GoogleOneTap: React.FC<GoogleOneTapProps> = ({
  clientId,
  callback,
  loginUri,
  context = 'signin',
  cancelOnTapOutside = true,
  autoSelect = false,
  momentCallback,
}) => {
  useEffect(() => {
    // Initialize Google One Tap when the script is loaded
    const initializeGoogleOneTap = () => {
      if (window.google?.accounts?.id) {
        const config: any = {
          client_id: clientId,
          context,
          cancel_on_tap_outside: cancelOnTapOutside,
          auto_select: autoSelect,
        };

        if (callback) {
          config.callback = callback;
        }

        if (loginUri) {
          config.login_uri = loginUri;
        }

        if (momentCallback) {
          config.moment_callback = momentCallback;
        }

        window.google.accounts.id.initialize(config);
        (window.google.accounts.id.prompt as any)(momentCallback);
      }
    };

    // Check if Google accounts is already available
    if (window.google?.accounts?.id) {
      initializeGoogleOneTap();
    } else {
      // Wait for the script to load
      const checkGoogleLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogleLoaded);
          initializeGoogleOneTap();
        }
      }, 100);

      return () => clearInterval(checkGoogleLoaded);
    }
  }, [clientId, callback, loginUri, context, cancelOnTapOutside, autoSelect, momentCallback]);

  return (
    <Script
      src='https://accounts.google.com/gsi/client'
      strategy='afterInteractive'
    />
  );
};

export default GoogleOneTap;
